import { useEffect, useRef } from 'react';
import { ButtonSubType } from '../types';

export const ButtonPreview = ({ config, subType }: { config: any; subType: ButtonSubType }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (subType !== 'firefly') return;
    if (!wrapperRef.current || !buttonRef.current || !canvasRef.current) return;

    const button = buttonRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const borderWidth = config.borderWidth || 2;
    const fireflySize = config.fireflySize || 6;
    const fireflyGlow = config.fireflyGlow || 15;
    const fireflySpeed = config.fireflySpeed || 1;
    const fireflyColor = config.fireflyColor || '#00ffaa';

    const updateCanvas = () => {
      canvas.width = button.offsetWidth;
      canvas.height = button.offsetHeight;
    };

    updateCanvas();

    const resizeObserver = new ResizeObserver(() => {
      updateCanvas();
    });
    resizeObserver.observe(button);

    let animationId: number;

    const animate = () => {
      const btnWidth = button.offsetWidth;
      const btnHeight = button.offsetHeight;
      if (btnWidth <= 0 || btnHeight <= 0) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      const time = Date.now() * 0.001 * fireflySpeed;
      const speed = 30;
      const perimeter = (btnWidth + btnHeight) * 2;
      const position = (time * speed) % perimeter;

      let dotX = 0;
      let dotY = 0;

      if (position < btnWidth) {
        dotX = position;
        dotY = 0;
      } else if (position < btnWidth + btnHeight) {
        dotX = btnWidth;
        dotY = position - btnWidth;
      } else if (position < btnWidth * 2 + btnHeight) {
        dotX = btnWidth - (position - btnWidth - btnHeight);
        dotY = btnHeight;
      } else {
        dotX = 0;
        dotY = btnHeight - (position - btnWidth * 2 - btnHeight);
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const blinkAlpha = 0.7 + Math.sin(time * 10) * 0.3;

      ctx.globalAlpha = blinkAlpha * 0.5;
      const gradient = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, fireflyGlow * 2);
      gradient.addColorStop(0, fireflyColor);
      gradient.addColorStop(0.3, fireflyColor + '80');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(dotX, dotY, fireflyGlow * 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = blinkAlpha;
      ctx.fillStyle = fireflyColor;
      ctx.beginPath();
      ctx.arc(dotX, dotY, fireflySize, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = blinkAlpha * 0.9;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(dotX, dotY, fireflySize * 0.4, 0, Math.PI * 2);
      ctx.fill();

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      resizeObserver.disconnect();
    };
  }, [config, subType]);

  const borderWidth = config.borderWidth || 2;
  const borderRadius = config.borderRadius || 10;

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        display: 'inline-block',
        overflow: 'visible',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <button
        ref={buttonRef}
        style={{
          padding: '12px 24px',
          background: '#2a2a2a',
          border: `${borderWidth}px solid #444`,
          borderRadius: `${borderRadius}px`,
          color: '#fff',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1,
        }}
      >
        Click Me
      </button>
    </div>
  );
};
